import { getEmail } from '../../api/emailsApi/emails';
import { activityTimelineSaved, updateRecords } from '../actions/mail';
import { applyDocAssistantSavedFiles } from '../actions/files';

// Pull the freshly-saved records back from Salesforce so a matched/assigned matter renders with its
// real display name, its true activity-timeline status, and its reparented files marked saved (with
// docrioId and reparented name). Shared by the automatic-match poll and the manual "Add" flow so both
// reflect what actually happened in Salesforce rather than assuming success. Returns the number of
// saved records surfaced (0 when the backend reflects none), and no-ops safely on an empty response.
// `isCancelled` lets a caller drop the dispatches when the active email changed during the read, so a
// message switch cannot write this email's records into the next message's freshly reset state.
// `filesOnly` marks just the reparented files saved and leaves the caller's own record/timeline
// dispatches authoritative (the manual multi-matter assign sets timeline status from the assign
// response, but still needs the files flagged saved so Save does not re-upload them).
export const refreshSavedLocations = async (dispatch, { emailId, ownerEmail, docrioNamespace = 'litify_docs', filesOnly = false, isCancelled } = {}) => {
  const emailResponse = await getEmail(emailId, '', true, ownerEmail);
  if (isCancelled && isCancelled()) {
    return 0;
  }
  const emailAndDisplayRecords = emailResponse?.data || {};
  const savedRecords = emailAndDisplayRecords.EmailMessage
    ? [emailAndDisplayRecords.EmailMessage]
    : Object.values(emailAndDisplayRecords.Results || {});
  if (!savedRecords.length) {
    return 0;
  }
  const hashField = `${docrioNamespace}__File_Data_Hash__c`;
  const fileInfoField = `${docrioNamespace}__File_Info__c`;
  const nameField = `${docrioNamespace}__File_Info__r.Name`;
  const relatedToField = `${docrioNamespace}__File_Info__r.${docrioNamespace}__Related_To__c`;
  const folderPathField = `${docrioNamespace}__File_Info__r.${docrioNamespace}__Folder_Path__c`;
  const savedStatus = {};
  const selectedRecords = [];
  const savedFiles = [];
  savedRecords.forEach((relatedTo) => {
    savedStatus[relatedTo.id] = relatedTo.emailMessageRecordSaved;
    (relatedTo.attachments || []).forEach((attachment) => {
      // The file was reparented to the matter; build the folderPaths entry the Location count and
      // folder row read, keyed by the matter id like the load path does.
      const parentId = attachment[relatedToField];
      const parsedPath = JSON.parse(attachment[folderPathField] || '[]');
      const folderPaths = parentId ? {
        [parentId]: {
          path: parsedPath.join('/'),
          name: parsedPath.length ? parsedPath[parsedPath.length - 1] : '',
          subfolders: [],
        },
      } : {};
      savedFiles.push({
        hash: attachment[hashField],
        docrioId: attachment[fileInfoField],
        name: attachment[nameField],
        folderPaths,
      });
    });
    selectedRecords.push({
      ...relatedTo,
      Id: relatedTo.id,
      type: relatedTo.relatedToApiName,
      autothreading: emailAndDisplayRecords.threadSave?.includes(relatedTo.id),
      ...relatedTo.displayRecords[0],
    });
  });
  if (!filesOnly) {
    dispatch(updateRecords(selectedRecords));
    dispatch(activityTimelineSaved(savedStatus));
  }
  if (savedFiles.length) {
    dispatch(applyDocAssistantSavedFiles(savedFiles));
  }
  return savedRecords.length;
};
